// Client-side mirror of the server's tool registry. These Sets drive the UI
// contract — which proposal cards offer Preview/Apply (slide tools) and which
// mutate the deck manifest (deck tools). They must track the server:
//   - SLIDE_TOOL_NAMES mirrors SLIDE_EDIT_TOOLS in src/server/llm/tools.ts,
//     minus read-only tools like `read_other_slide` (nothing to apply).
//     `replace_slide_content` is the retired predecessor of `edit_slide` —
//     kept so cards in persisted threads still render their actions.
//   - DECK_TOOL_NAMES mirrors DECK_EDIT_TOOLS in src/server/llm/deck-tools.ts.
//   - `edit_slide` is deliberately in BOTH (one content tool, two scopes);
//     ToolCard routes apply/reject by the card's own thread, not by set.
// If the server adds or renames an editing tool, update these too.
export const SLIDE_TOOL_NAMES = new Set(['edit_slide', 'replace_slide_content', 'swap_primitive']);

export const DECK_TOOL_NAMES = new Set([
  'create_slide',
  'edit_slide',
  'delete_slide',
  'reorder_slides',
  'reorder_sections',
  'create_section',
  'delete_section',
]);

/**
 * Has this proposal card been dealt with? THE single answer — every surface
 * that asks must ask here.
 *
 * A card is one object shown in two places (the conversation and the Activity
 * tab), and each place used to test the statuses it happened to know about.
 * `reverted` was added for revert-after-apply and only ToolCard learned it, so
 * the conversation still counted reverted cards as pending: the "Apply all (N)"
 * bar offered to re-apply them, they never collapsed to "Resolved", and — worst
 * — a lone reverted card was eligible for auto-preview, writing the proposal
 * back onto the slide as though the revert had undone itself (Jake, 2026-07-30).
 *
 * `block.applied` is the legacy boolean that predates `status`; threads written
 * before it still carry it, so it counts too.
 */
/** The fields this reads off a card — a structural minimum, not the whole
 *  block, so every surface holding one can pass it. `status` stays a plain
 *  string on purpose: an unknown status must reach the "not resolved" branch
 *  below rather than fail to compile, which is the same fail-safe direction
 *  the function itself takes. */
export type ResolvableCard = {
  applied?: boolean;
  status?: string;
  [key: string]: unknown;
};

export function isCardResolved(block: ResolvableCard | null | undefined): boolean {
  if (!block) return false;
  return (
    Boolean(block.applied) || block.status === 'applied' || block.status === 'rejected' || block.status === 'reverted'
  );
}
